/**
 * Pricing page — Free / Pro / All Access tier comparison for the
 * CareerForge platform (seven integrated products).
 *
 * Anchor strategy: position against career coaches and assembled tool
 * stacks, not against single-product subscriptions like Teal or Rezi.
 *
 * States handled:
 * 1. Not logged in     → "Get Started Free" / "Upgrade" CTAs → /login
 * 2. Free tier         → Upgrade to Pro or All Access → Stripe Checkout
 * 3. Pro tier          → Upgrade to All Access, or Manage Subscription
 * 4. All Access tier   → Manage Subscription
 * 5. /billing/success  → Success banner after Stripe checkout
 *
 * Lead banner: value-anchor card ("a full year of tools for less than one
 * coaching session"). The founding-member 33%-off promo has been retired.
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { useAuth } from "../hooks/useAuth";
import { createCheckoutSession, createPortalSession } from "../api/client";
import { toUserSafeErrorMessage } from "../utils/errorMessage";
import {
  track,
  useScrollDepth,
  usePageTimer,
  useExitTracking,
  useIntersectionTracker,
  useAllAccessDwellTimer,
} from "../utils/analytics";
import {
  PRICING,
  ANNUAL_DISCOUNT_PCT,
  FREE_EVALS_PER_MONTH,
  fmtUSD,
} from "../constants/pricing";

// ---------------------------------------------------------------------------
// Feature lists
// ---------------------------------------------------------------------------

const FREE_FEATURE_KEYS = [
  "PricingPage.freeFeature1",
  "PricingPage.freeFeature2",
  "PricingPage.freeFeature3",
  "PricingPage.freeFeature4",
  "PricingPage.freeFeature5",
];

const PRO_FEATURE_KEYS = [
  "PricingPage.proFeature1",
  "PricingPage.proFeature2",
  "PricingPage.proFeature3",
  "PricingPage.proFeature4",
  "PricingPage.proFeature5",
  "PricingPage.proFeature6",
  "PricingPage.proFeature7",
];

const ALL_ACCESS_FEATURE_KEYS = [
  "PricingPage.allAccessFeature1",
  "PricingPage.allAccessFeature2",
  "PricingPage.allAccessFeature3",
  "PricingPage.allAccessFeatureAgenticOps",
  "PricingPage.allAccessFeatureHarness",
  "PricingPage.allAccessFeature4",
  "PricingPage.allAccessFeature5",
  "PricingPage.allAccessFeature6",
  "PricingPage.allAccessFeature7",
  "PricingPage.allAccessFeature8",
  "PricingPage.allAccessFeature9",
];

// "Assemble the stack" comparison — the per-product subscriptions a buyer
// would otherwise stack to approximate CareerForge. Prices verified from
// public competitor pricing pages, April 2026. Update annually or when
// any single competitor materially changes price.
const STACK_COMPARISON = [
  { name: "Exponent", price: 79, coversKey: "PricingPage.stackCoversExponent" },
  { name: "Teal HQ", price: 29, coversKey: "PricingPage.stackCoversTealHq" },
  { name: "Interview Query", price: 79, coversKey: "PricingPage.stackCoversInterviewQuery" },
  { name: "LeetCode Premium", price: 35, coversKey: "PricingPage.stackCoversLeetcode" },
  { name: "Taplio", price: 49, coversKey: "PricingPage.stackCoversTaplio" },
  { name: "DeepLearning.AI (Coursera)", price: 49, coversKey: "PricingPage.stackCoversDeeplearning" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type Cadence = "monthly" | "annual";

function effectiveMonthly(annualTotal: number): number {
  return Math.round(annualTotal / 12);
}

function priceLabel(
  tier: "pro" | "allAccess",
  cadence: Cadence,
  t: TFunction,
): {
  primary: string;
  secondary: string;
} {
  const cfg = PRICING[tier];
  if (cadence === "monthly") {
    return { primary: fmtUSD(cfg.monthly), secondary: t("PricingPage.perMonthSuffix") };
  }
  return {
    primary: fmtUSD(effectiveMonthly(cfg.annual)),
    secondary: t("PricingPage.perMonthAnnualSuffix"),
  };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function PricingPage() {
  const { t } = useTranslation("pages-products");
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cadence, setCadence] = useState<Cadence>("monthly");

  const isSuccess = location.pathname === "/billing/success";
  const currentTier = user?.plan_tier ?? "free";
  const isAllAccess = currentTier === "all_access";
  const isPro = currentTier === "pro";

  // ── Analytics ─────────────────────────────────────────────────────────────
  // §2a: checkout success — fired once when Stripe redirects back to /billing/success
  useEffect(() => {
    if (!isSuccess) return;
    const params = new URLSearchParams(location.search);
    track("pricing.checkout_success", {
      tier: params.get("tier") ?? "unknown",
      cadence: params.get("cadence") ?? "unknown",
    });
  }, [isSuccess, location.search]);

  // §2a: page view + traffic source (referrer captures channel intent)
  useEffect(() => {
    track("pricing.page_view", {
      referrer: document.referrer || null,
      traffic_source: document.referrer
        ? new URL(document.referrer, window.location.href).hostname
        : "direct",
      plan_tier: currentTier,
    });
  // Fire once on mount; currentTier is stable on load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // §2a: scroll depth — 25 / 50 / 75 / 100 %
  useScrollDepth("pricing.scroll_depth");

  // §2a: time-on-page (overall)
  usePageTimer("pricing.time_on_page");

  // §2a: exit events (tab hidden / beforeunload)
  useExitTracking("pricing.exit");

  // §2a: All Access section visibility + dwell timer
  const [allAccessVisible, setAllAccessVisible] = useState(false);
  const allAccessSectionRef = useIntersectionTracker(
    "pricing.all_access_seen",
    { plan_tier: currentTier },
  );
  // Wire the intersection-visible flag for the dwell timer.
  // We piggyback on a separate IntersectionObserver so the tracker hook stays
  // single-responsibility (fires-once event vs continuous state).
  const allAccessDwellObserverRef = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    const el = allAccessSectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    allAccessDwellObserverRef.current = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAllAccessVisible(true); },
      { threshold: 0.25 },
    );
    allAccessDwellObserverRef.current.observe(el);
    return () => allAccessDwellObserverRef.current?.disconnect();
  // allAccessSectionRef is stable; this runs once after mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useAllAccessDwellTimer("pricing.all_access_dwell", allAccessVisible);
  // ── End Analytics ──────────────────────────────────────────────────────────

  const handleCheckout = async (
    tier: "pro" | "all_access",
    cadenceArg: Cadence = cadence,
    promo: string | null = null,
  ) => {
    // §2a: CTA click-through — fired before the navigation so it's not lost
    track("pricing.cta_click", {
      tier,
      cadence: cadenceArg,
      promo: promo ?? null,
      authenticated: isAuthenticated,
      current_plan: currentTier,
    });

    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/pricing" } });
      return;
    }
    setLoadingTier(`${tier}-${cadenceArg}${promo ? `-${promo}` : ""}`);
    setError(null);
    try {
      const { checkout_url } = await createCheckoutSession({
        tier,
        cadence: cadenceArg,
        promo,
      });
      window.location.href = checkout_url;
    } catch (err: any) {
      setError(toUserSafeErrorMessage(err, t("PricingPage.checkoutFailedError")));
    } finally {
      setLoadingTier(null);
    }
  };

  const handleManageSubscription = async () => {
    // §2a: CTA click — nav-away to portal (not a checkout)
    track("pricing.cta_click", {
      tier: currentTier,
      cadence,
      action: "manage_subscription",
      authenticated: true,
      current_plan: currentTier,
    });
    setLoadingTier("portal");
    setError(null);
    try {
      const { portal_url } = await createPortalSession();
      window.location.href = portal_url;
    } catch (err: any) {
      setError(toUserSafeErrorMessage(err, t("PricingPage.portalFailedError")));
    } finally {
      setLoadingTier(null);
    }
  };

  const proLabel = priceLabel("pro", cadence, t);
  const allAccessLabel = priceLabel("allAccess", cadence, t);
  const stackTotal = STACK_COMPARISON.reduce((sum, s) => sum + s.price, 0);

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", py: 2, px: { xs: 2, sm: 3 } }}>
      {/* ── Success banner ──────────────────────────────────── */}
      {isSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Trans
            i18nKey="PricingPage.checkoutSuccessNotice"
            t={t}
            components={[<strong key="strong0" />]}
          />
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Header ──────────────────────────────────────────── */}
      <Stack alignItems="center" sx={{ mb: 4 }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/favicon_1.png`}
          alt={t("PricingPage.logoAlt")}
          sx={{ width: 56, height: 56, objectFit: "contain", mb: 2 }}
        />
        <Typography
          variant="h4"
          component="h1"
          fontWeight={500}
          textAlign="center"
          gutterBottom
          sx={{ fontSize: { xs: "1.6rem", sm: "2.2rem" }, letterSpacing: "-0.02em" }}
        >
          {t("PricingPage.pageTitle")}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          textAlign="center"
          sx={{ maxWidth: 620, mx: "auto" }}
        >
          <Trans
            i18nKey="PricingPage.pageIntro"
            t={t}
            values={{ allAccessPrice: fmtUSD(PRICING.allAccess.monthly) }}
            components={[<strong key="strong0" />, <strong key="strong1" />]}
          />
        </Typography>
      </Stack>

      {/* ── Founder Pricing banner ──────────────────────────── */}
      {!isPro && !isAllAccess && (
        <Card
          elevation={0}
          sx={{
            mb: 4,
            borderRadius: 3,
            borderLeft: `6px solid ${theme.palette.secondary.main}`,
            bgcolor: alpha(theme.palette.secondary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.25)}`,
          }}
        >
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <LocalOfferIcon color="secondary" sx={{ fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {t("PricingPage.founderBannerTitle")}
                  </Typography>
                  <Chip
                    label={t("PricingPage.bestValueChipLabel")}
                    size="small"
                    color="secondary"
                    sx={{ height: 20, fontSize: "0.7rem", fontWeight: 500 }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t("PricingPage.founderBannerBody")}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ── Cadence toggle ──────────────────────────────────── */}
      <Stack alignItems="center" sx={{ mb: 4 }}>
        <ToggleButtonGroup
          value={cadence}
          exclusive
          onChange={(_, val) => val && setCadence(val)}
          size="small"
          color="primary"
        >
          <ToggleButton value="monthly" sx={{ px: 3, textTransform: "none", fontWeight: 500 }}>
            {t("PricingPage.monthlyToggle")}
          </ToggleButton>
          <ToggleButton value="annual" sx={{ px: 3, textTransform: "none", fontWeight: 500 }}>
            {t("PricingPage.annualToggle")}
            <Chip
              label={t("PricingPage.saveAnnualDiscountChipLabel", { percent: ANNUAL_DISCOUNT_PCT })}
              size="small"
              color="secondary"
              sx={{ ml: 1, height: 20, fontSize: "0.7rem", fontWeight: 500 }}
            />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* ── Tier cards ──────────────────────────────────────── */}
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={3}
        justifyContent="center"
        alignItems="stretch"
      >
        {/* ── FREE ── */}
        <Card
          elevation={0}
          sx={{
            flex: 1,
            border: `2px solid ${currentTier === "free" ? theme.palette.primary.main : theme.palette.divider}`,
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 4, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box>
              <Typography variant="h5" fontWeight={500} gutterBottom>{t("PricingPage.freeTierName")}</Typography>
              <Stack direction="row" alignItems="baseline" spacing={0.5} mb={0.5}>
                <Typography variant="h3" fontWeight={500}>$0</Typography>
                <Typography variant="body1" color="text.secondary">{t("PricingPage.foreverSuffix")}</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {t("PricingPage.freeTierTagline")}
              </Typography>
              {currentTier === "free" && isAuthenticated && (
                <Chip label={t("PricingPage.currentPlanChipLabel")} color="primary" size="small" sx={{ mb: 2 }} />
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <List dense disablePadding sx={{ flex: 1 }}>
              {FREE_FEATURE_KEYS.map((key) => (
                <ListItem key={key} disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(key, { count: FREE_EVALS_PER_MONTH })}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              ))}
            </List>

            <Box mt={3}>
              {!isAuthenticated && (
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={() => {
                    // §2a: CTA click — Free tier nav-away
                    track("pricing.cta_click", {
                      tier: "free",
                      cadence,
                      action: "get_started_free",
                      authenticated: false,
                      current_plan: currentTier,
                    });
                    navigate("/login");
                  }}
                >
                  {t("PricingPage.getStartedFreeButton")}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* ── PRO ── */}
        <Card
          elevation={0}
          sx={{
            flex: 1,
            border: `2px solid ${isPro ? theme.palette.primary.main : theme.palette.divider}`,
            borderRadius: 3,
            position: "relative",
            overflow: "visible",
          }}
        >
          <CardContent sx={{ p: 4, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box>
              <Typography variant="h5" fontWeight={500} color="primary.main" gutterBottom>{t("PricingPage.proTierName")}</Typography>
              <Stack direction="row" alignItems="baseline" spacing={0.5} mb={0.5}>
                <Typography variant="h3" fontWeight={500}>{proLabel.primary}</Typography>
                <Typography variant="body1" color="text.secondary">{proLabel.secondary}</Typography>
              </Stack>
              {cadence === "annual" && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  {t("PricingPage.billedAnnuallyCaption", {
                    yearlyPrice: fmtUSD(PRICING.pro.annual),
                    savings: fmtUSD(PRICING.pro.monthly * 12 - PRICING.pro.annual),
                  })}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" mb={2}>
                {t("PricingPage.proTierTagline")}
              </Typography>
              {isPro && <Chip label={t("PricingPage.currentPlanChipLabel")} color="primary" size="small" sx={{ mb: 2 }} />}
            </Box>

            <Divider sx={{ my: 2 }} />

            <List dense disablePadding sx={{ flex: 1 }}>
              {PRO_FEATURE_KEYS.map((key) => (
                <ListItem key={key} disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t(key)} primaryTypographyProps={{ variant: "body2" }} />
                </ListItem>
              ))}
            </List>

            <Box mt={3}>
              {isAllAccess ? (
                <Button variant="outlined" fullWidth size="large" disabled>
                  {t("PricingPage.includedInAllAccessButton")}
                </Button>
              ) : isPro ? (
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  onClick={handleManageSubscription}
                  disabled={loadingTier === "portal"}
                >
                  {loadingTier === "portal" ? <CircularProgress size={22} /> : t("PricingPage.manageSubscriptionButton")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<RocketLaunchIcon />}
                  onClick={() => handleCheckout("pro")}
                  disabled={!!loadingTier}
                  sx={{ py: 1.5 }}
                >
                  {loadingTier?.startsWith("pro") ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    t("PricingPage.upgradeToProButton")
                  )}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* ── ALL ACCESS ── */}
        {/* allAccessSectionRef: intersection tracker for §2a scroll-depth + dwell signals */}
        <Card
          ref={allAccessSectionRef}
          elevation={0}
          sx={{
            flex: 1,
            border: `2px solid ${isAllAccess ? theme.palette.warning.main : alpha(theme.palette.warning.main, 0.4)}`,
            borderRadius: 3,
            position: "relative",
            overflow: "visible",
            background: isAllAccess
              ? `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.06)} 0%, ${alpha(theme.palette.warning.main, 0.02)} 100%)`
              : undefined,
          }}
        >
          <Chip
            icon={<EmojiEventsIcon />}
            label={t("PricingPage.mostPopularChipLabel")}
            size="small"
            sx={{
              position: "absolute",
              top: -14,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1,
              bgcolor: theme.palette.warning.main,
              color: theme.palette.background.paper,
              fontWeight: 500,
              "& .MuiChip-icon": { color: theme.palette.background.paper },
            }}
          />
          <CardContent sx={{ p: 4, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box>
              <Typography variant="h5" fontWeight={500} sx={{ color: theme.palette.warning.dark }} gutterBottom>
                {t("PricingPage.allAccessTierName")}
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={0.5} mb={0.5}>
                <Typography variant="h3" fontWeight={500}>{allAccessLabel.primary}</Typography>
                <Typography variant="body1" color="text.secondary">{allAccessLabel.secondary}</Typography>
              </Stack>
              {cadence === "annual" && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  {t("PricingPage.billedAnnuallyCaption", {
                    yearlyPrice: fmtUSD(PRICING.allAccess.annual),
                    savings: fmtUSD(PRICING.allAccess.monthly * 12 - PRICING.allAccess.annual),
                  })}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" mb={2}>
                {t("PricingPage.allAccessTierTagline")}
              </Typography>
              {isAllAccess && (
                <Chip
                  label={t("PricingPage.currentPlanChipLabel")}
                  size="small"
                  sx={{ mb: 2, bgcolor: alpha(theme.palette.warning.main, 0.15), color: theme.palette.warning.dark, fontWeight: 500 }}
                />
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Pro features included */}
            <List dense disablePadding>
              {PRO_FEATURE_KEYS.map((key) => (
                <ListItem key={key} disableGutters sx={{ py: 0.35 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText primary={t(key)} primaryTypographyProps={{ variant: "body2", color: "text.secondary" }} />
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 1.5 }} />

            {/* All Access extras */}
            <List dense disablePadding sx={{ flex: 1 }}>
              {ALL_ACCESS_FEATURE_KEYS.map((key) => (
                <ListItem key={key} disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <EmojiEventsIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(key)}
                    primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                  />
                </ListItem>
              ))}
            </List>

            <Box mt={3}>
              {isAllAccess ? (
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  onClick={handleManageSubscription}
                  disabled={loadingTier === "portal"}
                  sx={{ borderColor: theme.palette.warning.main, color: theme.palette.warning.dark, "&:hover": { borderColor: theme.palette.warning.main, bgcolor: alpha(theme.palette.warning.main, 0.06) } }}
                >
                  {loadingTier === "portal" ? <CircularProgress size={22} /> : t("PricingPage.manageSubscriptionButton")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<EmojiEventsIcon />}
                  onClick={() => handleCheckout("all_access")}
                  disabled={!!loadingTier}
                  sx={{
                    py: 1.5,
                    bgcolor: theme.palette.warning.main,
                    "&:hover": { bgcolor: theme.palette.warning.dark },
                    fontWeight: 500,
                  }}
                >
                  {loadingTier?.startsWith("all_access") ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : isPro ? (
                    t("PricingPage.upgradeToAllAccessButton")
                  ) : (
                    t("PricingPage.getAllAccessButton")
                  )}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Stack>

      {/* ── Assemble the Stack comparison ───────────────────── */}
      <Box sx={{ mt: 8, mb: 4 }}>
        <Stack alignItems="center" sx={{ mb: 4 }}>
          <Typography
            variant="overline"
            color="secondary.main"
            sx={{ fontWeight: 700, letterSpacing: "0.08em" }}
          >
            {t("PricingPage.whyOneBundleEyebrow")}
          </Typography>
          <Typography
            variant="h4"
            fontWeight={500}
            textAlign="center"
            sx={{ fontSize: { xs: "1.5rem", sm: "1.9rem" }, letterSpacing: "-0.02em", mt: 0.5 }}
          >
            {t("PricingPage.buildYourOwnStackTitle")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ maxWidth: 560, mt: 1 }}
          >
            {t("PricingPage.stackComparisonIntro", { stackTotal })}
          </Typography>
        </Stack>

        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            maxWidth: 720,
            mx: "auto",
            bgcolor: "background.paper",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <List disablePadding>
              {STACK_COMPARISON.map((item, i) => (
                <Box key={item.name}>
                  <ListItem
                    disableGutters
                    sx={{
                      py: 1.25,
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: { xs: 0.5, sm: 0 },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(item.coversKey)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, fontFamily: "monospace", flexShrink: 0 }}
                    >
                      ${item.price}/mo
                    </Typography>
                  </ListItem>
                  {i < STACK_COMPARISON.length - 1 && <Divider />}
                </Box>
              ))}
              <Divider sx={{ borderWidth: 2, my: 1 }} />
              <ListItem
                disableGutters
                sx={{ py: 1.5, flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.5, sm: 0 } }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t("PricingPage.stackTotalLabel")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("PricingPage.stackTotalDescription")}
                  </Typography>
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    fontFamily: "monospace",
                    color: "text.secondary",
                    textDecoration: "line-through",
                  }}
                >
                  ${stackTotal}/mo
                </Typography>
              </ListItem>
              <ListItem
                disableGutters
                sx={{
                  py: 1.5,
                  px: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 0.5, sm: 0 },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: "secondary.main" }}>
                    {t("PricingPage.careerForgeAllAccessLabel")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("PricingPage.careerForgeAllAccessDescription")}
                  </Typography>
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 500, fontFamily: "monospace", color: "secondary.main" }}
                >
                  ${PRICING.allAccess.monthly}/mo
                </Typography>
              </ListItem>
            </List>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 2, textAlign: "center" }}
            >
              {t("PricingPage.competitorPricesDisclaimer")}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* ── Usage info ──────────────────────────────────────── */}
      {isAuthenticated && user && (
        <Box sx={{ mt: 4, p: 3, bgcolor: "background.paper", borderRadius: 2, textAlign: "center", border: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" color="text.secondary">
            {currentTier === "free"
              ? t("PricingPage.usageThisMonthFreeTier", {
                  count: user.evaluations_this_month ?? 0,
                  limit: FREE_EVALS_PER_MONTH,
                })
              : t("PricingPage.usageThisMonthUnlimited", {
                  count: user.evaluations_this_month ?? 0,
                })}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
